import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';
import useAuth from '../hooks/useAuth';
import paymentService from '../services/paymentService';

const SubscriptionContext = createContext({});

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { user, userProfile } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch subscription plans
  const fetchPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('monthly_price', { ascending: true });

      if (error) {
        console.error('Error fetching plans from DB:', error);
        throw error;
      }
      
      if (!data || data.length === 0) {
        console.warn('No subscription plans found in database, using fallback data');
        // Fallback to hardcoded plans if database is empty
        const fallbackPlans = [
          {
            id: '1',
            tier: 'free',
            name: 'Free Tier',
            monthly_price: 0,
            annual_price: 0,
            features: {
              max_hires: 2,
              hires_per_month: 5,
              active_job_posts: 2,
              bookmark_workers: true,
              job_approval_time: '2 days',
              view_applications: true,
              ai_recommendations: false,
              matchmaking_access: false,
              interview_scheduling: false,
              max_applications_per_job: 30,
              ai_job_posts_generation_credits: 2
            },
            is_active: true
          },
          {
            id: '2',
            tier: 'pro',
            name: 'Pro Tier',
            monthly_price: 65,
            annual_price: 37,
            features: {
              max_hires: 10,
              hires_per_month: 50,
              active_job_posts: 5,
              bookmark_workers: true,
              job_approval_time: 'instant',
              view_applications: true,
              ai_recommendations: true,
              matchmaking_access: true,
              interview_scheduling: true,
              max_applications_per_job: 100,
              ai_job_posts_generation_credits: 10
            },
            is_active: true
          },
          {
            id: '3',
            tier: 'premium',
            name: 'Premium Tier',
            monthly_price: 95,
            annual_price: 55,
            features: {
              max_hires: 50,
              hires_per_month: 100,
              active_job_posts: 10,
              bookmark_workers: true,
              job_approval_time: 'instant',
              view_applications: true,
              ai_recommendations: true,
              matchmaking_access: true,
              interview_scheduling: true,
              max_applications_per_job: 200,
              ai_job_posts_generation_credits: 20
            },
            is_active: true
          },
          {
            id: '4',
            tier: 'agency',
            name: 'Agency Tier',
            monthly_price: 125,
            annual_price: 72,
            features: {
              max_hires: -1,
              hires_per_month: -1,
              active_job_posts: 50,
              bookmark_workers: true,
              job_approval_time: 'instant',
              view_applications: true,
              ai_recommendations: true,
              matchmaking_access: true,
              interview_scheduling: true,
              max_applications_per_job: -1,
              ai_job_posts_generation_credits: -1
            },
            is_active: true
          }
        ];
        setPlans(fallbackPlans);
        return;
      }
      
      setPlans(data);
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(err.message);
      
      // Use fallback data on error
      const fallbackPlans = [
        {
          id: '1',
          tier: 'free',
          name: 'Free Tier',
          monthly_price: 0,
          annual_price: 0,
          features: {
            max_hires: 2,
            hires_per_month: 5,
            active_job_posts: 2,
            bookmark_workers: true,
            job_approval_time: '2 days',
            view_applications: true,
            ai_recommendations: false,
            matchmaking_access: false,
            interview_scheduling: false,
            max_applications_per_job: 30,
            ai_job_posts_generation_credits: 2
          },
          is_active: true
        },
        {
          id: '2',
          tier: 'pro',
          name: 'Pro Tier',
          monthly_price: 65,
          annual_price: 37,
          features: {
            max_hires: 10,
            hires_per_month: 50,
            active_job_posts: 5,
            bookmark_workers: true,
            job_approval_time: 'instant',
            view_applications: true,
            ai_recommendations: true,
            matchmaking_access: true,
            interview_scheduling: true,
            max_applications_per_job: 100,
            ai_job_posts_generation_credits: 10
          },
          is_active: true
        },
        {
          id: '3',
          tier: 'premium',
          name: 'Premium Tier',
          monthly_price: 95,
          annual_price: 55,
          features: {
            max_hires: 50,
            hires_per_month: 100,
            active_job_posts: 10,
            bookmark_workers: true,
            job_approval_time: 'instant',
            view_applications: true,
            ai_recommendations: true,
            matchmaking_access: true,
            interview_scheduling: true,
            max_applications_per_job: 200,
            ai_job_posts_generation_credits: 20
          },
          is_active: true
        },
        {
          id: '4',
          tier: 'agency',
          name: 'Agency Tier',
          monthly_price: 125,
          annual_price: 72,
          features: {
            max_hires: -1,
            hires_per_month: -1,
            active_job_posts: 50,
            bookmark_workers: true,
            job_approval_time: 'instant',
            view_applications: true,
            ai_recommendations: true,
            matchmaking_access: true,
            interview_scheduling: true,
            max_applications_per_job: -1,
            ai_job_posts_generation_credits: -1
          },
          is_active: true
        }
      ];
      setPlans(fallbackPlans);
    }
  };

  // Fetch user subscription
  const fetchUserSubscription = async () => {
    if (!user) return;

    try {
      // Fetch the user subscription - include both active and cancelled to show proper status
      const { data: subscriptionData, error: subscriptionError } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .in('status', ['active', 'canceled', 'expired'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (subscriptionError) throw subscriptionError;
      
      // If no subscription found, user defaults to free tier
      if (!subscriptionData || subscriptionData.length === 0) {
        setSubscription(null);
        return;
      }

      // Get the subscription plan details
      const subscription = subscriptionData[0];
      const { data: planData, error: planError } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('id', subscription.plan_id)
        .single();

      if (planError) throw planError;

      // Combine subscription and plan data
      setSubscription({
        ...subscription,
        subscription_plans: planData
      });
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.message);
    }
  };

  // Fetch current month usage
  const fetchUsage = async () => {
    if (!user) return;

    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format

    try {
      let { data, error } = await supabase
        .from('subscription_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('month_year', currentMonth)
        .maybeSingle(); // Returns null data (no error) for 0 rows, avoids HTTP 406

      if (error) throw error;

      if (!data) {
        // Create usage record if it doesn't exist, upsert avoids 409 on duplicate
        const { data: newUsage, error: upsertError } = await supabase
          .from('subscription_usage')
          .upsert({
            user_id: user.id,
            month_year: currentMonth,
            hires_count: 0,
            active_jobs_count: 0,
            communications_count: 0
          }, { onConflict: 'user_id,month_year', ignoreDuplicates: true })
          .select()
          .single();

        if (upsertError) throw upsertError;
        data = newUsage;
      }

      setUsage(data);
    } catch (err) {
      console.error('Error fetching usage:', err);
      setError(err.message);
    }
  };

  // Get total lifetime hires for the user
  const getTotalLifetimeHires = async () => {
    if (!user) return 0;

    try {
      const { data, error } = await supabase
        .from('job_applications')
        .select('job:jobs!inner(employer_id)')
        .in('status', ['accepted', 'hired'])
        .eq('job.employer_id', user.id);

      if (error) throw error;
      return data?.length || 0;
    } catch (err) {
      console.error('Error fetching total hires:', err);
      return 0;
    }
  };

  // Check if user has reached limits
  const checkLimit = async (limitType) => {
    // Get current plan (defaults to free if no active subscription)
    const plan = getCurrentPlan();
    if (!plan) return { allowed: false, remaining: 0 };

    const limits = plan.features;
    
    // If no usage data yet, assume zero usage
    const currentUsage = usage || {
      hires_count: 0,
      active_jobs_count: 0,
      communications_count: 0
    };
    
    switch (limitType) {
      case 'hires': {
        const hiresLimit = limits.hires_per_month;
        if (hiresLimit === -1) return { allowed: true, remaining: -1 }; // Unlimited
        if (hiresLimit === undefined || hiresLimit === null) {
          return { allowed: false, remaining: 0 };
        }
        const remaining = hiresLimit - (currentUsage.hires_count || 0);
        return { allowed: remaining > 0, remaining: Math.max(0, remaining) };
      }
      
      case 'max_hires': {
        const maxHiresLimit = limits.max_hires;
        if (maxHiresLimit === -1) return { allowed: true, remaining: -1 }; // Unlimited
        if (maxHiresLimit === undefined || maxHiresLimit === null) {
          return { allowed: false, remaining: 0 };
        }
        
        const totalHires = await getTotalLifetimeHires();
        const remaining = maxHiresLimit - totalHires;
        return { allowed: remaining > 0, remaining: Math.max(0, remaining) };
      }
      
      case 'active_jobs': {
        const jobsLimit = limits.active_job_posts;
        if (jobsLimit === -1) return { allowed: true, remaining: -1 }; // Unlimited
        if (jobsLimit === undefined || jobsLimit === null) {
          return { allowed: false, remaining: 0 };
        }
        const jobsRemaining = jobsLimit - (currentUsage.active_jobs_count || 0);
        return { allowed: jobsRemaining > 0, remaining: Math.max(0, jobsRemaining) };
      }
      
      case 'communications': {
        const commLimit = limits.hires_per_month; // Using same limit as hires
        if (commLimit === -1) return { allowed: true, remaining: -1 };
        if (commLimit === undefined || commLimit === null) {
          return { allowed: false, remaining: 0 };
        }
        const commRemaining = commLimit - (currentUsage.communications_count || 0);
        return { allowed: commRemaining > 0, remaining: Math.max(0, commRemaining) };
      }
      
      default:
        return { allowed: false, remaining: 0 };
    }
  };

  // Update usage
  const updateUsage = async (usageType, increment = 1) => {
    if (!user) return { success: false, error: 'No user found' };

    // Ensure usage record exists
    if (!usage) {
      await fetchUsage();
    }

    const currentUsage = usage || {
      hires_count: 0,
      active_jobs_count: 0,
      communications_count: 0
    };

    const currentMonth = new Date().toISOString().slice(0, 7);
    const updates = {};
    
    switch (usageType) {
      case 'hire':
        updates.hires_count = currentUsage.hires_count + increment;
        break;
      case 'job':
        updates.active_jobs_count = currentUsage.active_jobs_count + increment;
        break;
      case 'communication':
        updates.communications_count = currentUsage.communications_count + increment;
        break;
    }

    try {
      const { data, error } = await supabase
        .from('subscription_usage')
        .update(updates)
        .eq('user_id', user.id)
        .eq('month_year', currentMonth)
        .select()
        .single();

      if (error) throw error;
      setUsage(data);
      return { success: true };
    } catch (err) {
      console.error('Error updating usage:', err);
      return { success: false, error: err.message };
    }
  };

  // Get current plan features
  const getCurrentPlan = () => {
    // First priority: check userProfile.subscription_tier directly (source of truth from user_profiles table)
    if (userProfile?.subscription_tier && userProfile.subscription_tier !== 'free') {
      // Find the plan that matches the tier
      const matchingPlan = plans.find(plan => plan.tier === userProfile.subscription_tier);
      if (matchingPlan) {
        console.log('Using subscription tier from user profile:', userProfile.subscription_tier);
        return matchingPlan;
      }
    }
    
    // Second priority: If user has a subscription record, use that plan details
    if (subscription?.subscription_plans) {
      return subscription.subscription_plans;
    }
    
    // Third: Try to find free plan from database
    const freePlan = plans.find(plan => plan.tier === 'free');
    if (freePlan) {
      return freePlan;
    }
    
    // Fallback to hardcoded free plan if database plans not loaded yet
    return {
      tier: 'free',
      name: 'Free Tier',
      monthly_price: 0,
      annual_price: 0,
      features: {
        max_hires: 2,
        hires_per_month: 5,
        active_job_posts: 2,
        max_applications_per_job: 30,
        job_approval_time: '2 days',
        bookmark_workers: true,
        view_applications: true,
        matchmaking_access: false,
        interview_scheduling: false,
        ai_recommendations: false,
        ai_job_posts_generation_credits: 2
      }
    };
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchPlans(),
          fetchUserSubscription(),
          fetchUsage()
        ]);
      } catch (err) {
        console.error('Error loading subscription data:', err);
      } finally {
        setLoading(false);
      }
    };

    // Load plans regardless of authentication state to support pricing page
    loadData();
  }, [user?.id]);

  // Watch for changes to userProfile.subscription_tier and refresh if changed
  useEffect(() => {
    if (user?.id && userProfile?.subscription_tier) {
      // Just trigger a re-render by updating a ref, or fetch fresh subscription data
      const currentPlan = getCurrentPlan();
      if (currentPlan?.tier !== userProfile.subscription_tier) {
        console.log('Detected subscription tier change in profile:', userProfile.subscription_tier);
        // The getCurrentPlan() function will pick up the change automatically
      }
    }
  }, [userProfile?.subscription_tier, user?.id]);

  // Refetch all user data
  const refetchUserData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchUserSubscription(),
        fetchUsage()
      ]);
    } catch (error) {
      console.error('Error refetching user data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Cancel the user's active subscription
  const cancelSubscription = async () => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      console.log('Initiating subscription cancellation for user:', user.id);
      
      // Call the payment service to cancel the subscription
      const result = await paymentService.cancelSubscription(user.id);
      
      console.log('Subscription cancellation result:', result);

      // Refresh subscription data to reflect the cancellation
      await refetchUserData();

      return {
        success: true,
        message: 'Subscription canceled successfully',
        ...result
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to cancel subscription';
      console.error('Error in cancelSubscription:', errorMessage);
      setError(errorMessage);
      throw error;
    }
  };

  const value = {
    subscription,
    plans,
    usage,
    loading,
    error,
    checkLimit,
    updateUsage,
    getCurrentPlan,
    refetchSubscription: fetchUserSubscription,
    refetchUsage: fetchUsage,
    refetchUserData,
    cancelSubscription
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};